import { Component, Story, Accents, Knobs } from './DocTypes';

const PlaygroundStoryKnobs: Knobs = {
  label: { default: 'Are you sure?', editor: 'textbox' },
  checked: { default: true, editor: 'checkbox' },
  accent: { default: '', editor: 'dropdown', editorOptions: [''].concat(Accents) },
  disabled: { default: false, editor: 'checkbox' },
  rounded: { default: false, editor: 'checkbox' },
  threeState: { default: false, editor: 'checkbox' },
  size: { default: '', editor: 'dropdown', editorOptions: ['', 'normal', 'large'] },
  textAlign: { default: '', editor: 'dropdown', editorOptions: ['', 'left', 'right'] },
  name: { default: '', editor: 'textbox' },
  checkboxStyle: { default: '', editor: 'textbox' },
  labelStyle: { default: '', editor: 'textbox' },
};

const PlaygroundStoryKnobs_v1: Knobs = {
  ...PlaygroundStoryKnobs,
  id: { default: '', editor: 'textbox' },
};

const PlaygroundStoryKnobs_v1_React: Knobs = {
  ...PlaygroundStoryKnobs_v1,
  checkboxStyle: {
    ...PlaygroundStoryKnobs_v1.checkboxStyle,
    qualifierStart: '{',
    qualifierEnd: '}',
  },
  labelStyle: {
    ...PlaygroundStoryKnobs_v1.checkboxStyle,
    qualifierStart: '{',
    qualifierEnd: '}',
  },
};

const PlaygroundStoryKnobs_v3: Knobs = {
  ...PlaygroundStoryKnobs,
  checkboxId: { default: '', editor: 'textbox' },
  colourScheme: {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};

const PlaygroundStoryKnobs_v3_React: Knobs = {
  ...PlaygroundStoryKnobs_v3,
  checkboxStyle: {
    ...PlaygroundStoryKnobs_v3.checkboxStyle,
    qualifierStart: '{',
    qualifierEnd: '}',
  },
  labelStyle: {
    ...PlaygroundStoryKnobs_v3.checkboxStyle,
    qualifierStart: '{',
    qualifierEnd: '}',
  },
};

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'A checkbox where you can change all the attributes',
  coreComponent: {
    html: `<tag-checkbox{{...knobs}}></tag-checkbox>`,
    knobs: PlaygroundStoryKnobs_v1,
  },
  coreComponent_v3: {
    html: `<tag3-checkbox {{...knobs}}></tag3-checkbox>`,
    knobs: PlaygroundStoryKnobs_v3,
  },
  reactWrapper: {
    jsx: `<TagCheckbox{{...knobs}}/>`,
    knobs: PlaygroundStoryKnobs_v1_React,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Checkbox{{...knobs}}/>`,
    knobs: PlaygroundStoryKnobs_v3_React,
  },
};

const SuppressCheck: Story = {
  title: 'Suppress value change',
  description: 'An example of cancelling the checkbox value change event',
  coreComponent: {
    html: `<tag-checkbox{{...knobs}}></tag-checkbox>`,
    js: `
    var tagCheckbox = document.querySelector('tag-checkbox');
    tagCheckbox.componentOnReady().then(function() {
      tagCheckbox.addEventListener("valueChange", function(e) {
        const result = confirm('You would like to change the value to ' + e.detail.value + '?');
        e.detail.cancel = !result;
      });
    });`,
    knobs: PlaygroundStoryKnobs_v1,
  },
  coreComponent_v3: {
    html: `<tag3-checkbox{{...knobs}}></tag3-checkbox>`,
    js: `
    var tag3Checkbox = document.querySelector('tag3-checkbox');
      tag3Checkbox.addEventListener("checkboxValueChange", function(e) {
        const result3 = confirm('You would like to change the value to ' + e.detail.value + '?');
        e.detail.cancel = !result3;
    });`,
    knobs: PlaygroundStoryKnobs_v3,
  },
  reactWrapper: {
    jsx: `
    <TagCheckbox onValueChange={e => {
      const result = confirm('You would like to change the value to ' + e.detail.value + '?');
      e.detail.cancel = !result;
    }} {{...knobs}}/>`,
    knobs: PlaygroundStoryKnobs_v1_React,
  },
  reactWrapper_v3: {
    jsx: `
    <Tag3Checkbox onCheckboxValueChange={e => {
      const result = confirm('You would like to change the value to ' + e.detail.value + '?');
      e.detail.cancel = !result;
    }} {{...knobs}}/>`,
    knobs: PlaygroundStoryKnobs_v3_React,
  },
};

const FocusStory: Story = {
  title: 'Focus',
  description: 'An example of how to focus the checkbox',
  coreComponent: {
    html: `
    <tag-checkbox label="Are you sure?" checked></tag-checkbox>
    <tag-button text="Focus"></tag-button>
    `,
    js: `
    var button = document.querySelector('tag-button');
    var checkbox = document.querySelector('tag-checkbox');
    button.componentOnReady().then(function() {
      button.addEventListener('buttonClick', function() {
        checkbox.focusCheckbox();
      });
    });
    `,
  },
  coreComponent_v3: {
    html: `
    <tag3-checkbox label="Are you sure?" checked></tag3-checkbox>
    <tag3-button style="display:block">Focus</tag3-button>
    `,
    js: `
    var button = document.querySelector('tag3-button');
    var checkbox = document.querySelector('tag3-checkbox');
    button.addEventListener('buttonClick', function() {
      checkbox.focusCheckbox();
    })
    `,
  },
  reactWrapper: {
    jsx: `<Example />`,
    parentJSX: `Example = () => {
    setTimeout(()=>{
      var button = document.querySelector('tag-button');
      button.componentOnReady().then(function() {
        button.addEventListener('buttonClick', function() {
          var checkbox = document.querySelector('tag-checkbox');
          checkbox.focusCheckbox();
        });
      });
    },500);
    return (
      <div>
        <TagCheckbox label="Are you sure?"  checked />
        <TagButton text="Focus"/>
      </div>
    )
  };`,
  },
  reactWrapper_v3: {
    jsx: `<Example />`,
    parentJSX: `Example = () => {
  const checkbox = React.useRef(null);
  return (
    <div>
      <Tag3Checkbox ref={checkbox} label="Are you sure?" checked />
      <Tag3Button style={{display: 'block'}} text="Focus" onButtonClick={() => checkbox.current.focusCheckbox()} />
    </div>
  )
};`,
  },
};

export const TagCheckboxDocs: Component = {
  type: 'component',
  name: 'checkbox',
  description: 'Renders a checkbox',
  mobileFriendly: true,
  popular: true,
  preRelease: '',
  props: [
    {
      name: 'id',
      description: 'A unique for the checkbox. Deprecated in V3',
      type: 'string',
    },
    {
      name: 'checkboxId',
      description: 'V3 only: A unique id for the checkbox',
      type: 'string',
    },
    {
      name: 'name',
      description: 'A unique name for the checkbox',
      type: 'string',
    },
    {
      name: 'label',
      description: 'A label for the checkbox',
      type: 'string',
    },
    {
      name: 'checked',
      description: 'Whether the checkbox is checked or not',
      type: 'boolean',
    },
    {
      name: 'disabled',
      description: 'Whether the checkbox is disabled or not',
      type: 'boolean',
    },
    {
      name: 'accent',
      description: 'The colour of the checked state',
      type: 'string',
    },
    {
      name: 'rounded',
      description: 'Defines whether to render as a rounded checkbox',
      type: 'boolean',
    },
    {
      name: 'size',
      description: 'Defines the size of the checkbox',
      type: 'normal | large',
      default: 'normal',
    },
    {
      name: 'textAlign',
      description: 'Defines the alignment of the checkbox text',
      type: 'left | right',
      default: 'left',
    },
    {
      name: 'three-state',
      description: 'Whether the checkbox has 3 value states (true, false and undefined)',
      type: 'boolean',
    },
    {
      name: 'checkboxStyle',
      description: 'JSX style object in JSON to apply to the checkbox',
      type: 'string',
    },
    {
      name: 'labelStyle',
      description: 'JSX style object in JSON to apply to the label',
      type: 'string',
    },
    {
      name: 'colourScheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'string',
    },
    {
      name: 'device',
      description: 'V3 Only: set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'valueChange',
      description: 'V1 only: Raised when the checkbox is checked / unchecked. Deprecated in V3',
      parameters: [
        { name: 'value', description: 'The value', type: 'boolean' },
        { name: 'cancel', description: 'Cancel', type: 'boolean', default: 'false' },
      ],
    },
    {
      name: 'checkboxValueChange',
      description: 'V3 only: Raised when the checkbox is checked / unchecked',
      parameters: [
        { name: 'value', description: 'The value', type: 'boolean' },
        { name: 'cancel', description: 'Cancel', type: 'boolean', default: 'false' },
        { name: 'checkboxId', description: 'The checkbox id', type: 'string' },
        { name: 'name', description: 'The name', type: 'string' },
      ],
    },
    {
      name: 'checkboxBlur',
      description: 'Event raised when the checkbox loses focus',
      parameters: [
        {
          name: 'name',
          type: 'string',
          description: 'checkbox name',
        },
      ],
    },
    {
      name: 'checkboxFocus',
      description: 'Event raised when the checkbox gains focus',
      parameters: [
        {
          name: 'name',
          type: 'string',
          description: 'checkbox name',
        },
      ],
    },
  ],
  methods: [
    {
      name: 'focusCheckbox',
      description: 'Sets the focus to the checkbox',
      parameters: [],
      returnType: 'void',
    },
  ],
  stories: [PlaygroundStory, SuppressCheck, FocusStory],
};
